import isEqual from "lodash/isEqual";
import isNumber from "lodash/isNumber";
import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { useFormContext } from "react-hook-form";
import {
  SelectContent,
  SelectField,
  SelectFieldProps,
  SelectItem,
} from "@/components/atoms/Select";
import TimeIcon from "@/icons/__generated__/Time/Time20";
import { BookingScheduleTime } from "@/lib/types/schedule-types";
import { cn } from "@/lib/utils";
import {
  autoCorrectDuration,
  normalizeTimeData,
  TimeData,
} from "@/lib/utils/time-utils";
import { TimeSelectProps } from "./TimeSelect";

const defaultStartTimeProps = {
  placeholder: "Select start time",
  name: "startTime",
};

const defaultEndTimeProps = {
  placeholder: "Select end time",
  name: "endTime",
};

export type TimeSelectFieldProps = Omit<
  TimeSelectProps,
  "startTimeProps" | "endTimeProps" | "ref" | "value" | "onValueChange"
> & {
  startTimeProps?: Partial<Omit<SelectFieldProps, "renderContent">>;
  endTimeProps?: Partial<Omit<SelectFieldProps, "renderContent">>;
  displayTimeIcon?: boolean;
  contentClassName?: string;
  onValueChange?: (
    fieldName: string,
    timeObj: BookingScheduleTime | null,
  ) => void;
};

export type TimeSelectFieldRef = Record<string, HTMLDivElement | null>;

export const TimeSelectField = forwardRef<
  TimeSelectFieldRef,
  TimeSelectFieldProps
>(
  (
    {
      startTimeProps,
      endTimeProps,
      showEndTime = true,
      fullWidth = true,
      className,
      disabled,
      testId,
      timeOptions,
      onValueChange,
      displayTimeIcon = true,
      autoCorrectConfig,
      contentClassName,
      ...containerProps
    },
    ref,
  ) => {
    const { watch, setValue } = useFormContext();
    const selectItemRefs = useRef<Record<string, HTMLDivElement | null>>({});

    const finalStartTimeProps = {
      ...defaultStartTimeProps,
      ...(startTimeProps || {}),
    };

    const finalEndTimeProps = {
      ...defaultEndTimeProps,
      ...(endTimeProps || {}),
    };

    const startTimeOptions = timeOptions?.start
      ? normalizeTimeData(timeOptions.start || [])
      : TimeData;

    const endTimeOptions = timeOptions?.end
      ? normalizeTimeData(timeOptions.end || [])
      : TimeData;

    const selectedStartTime = watch(finalStartTimeProps.name);
    const selectedEndTime = watch(finalEndTimeProps.name);

    useImperativeHandle(ref, () => selectItemRefs.current);

    const getFirstAvailableEndTime = () => {
      return endTimeOptions.find((time) => time.available !== false);
    };

    const getAutoCorrectedValue = (value: TimeSelectProps["value"]) => {
      if (!autoCorrectConfig) {
        return value;
      }

      return autoCorrectDuration({
        config: autoCorrectConfig,
        value: value,
        timeOptions: {
          start: startTimeOptions,
          end: endTimeOptions,
        },
      });
    };

    const handleStartTimeChange = (newStartTimeIndex: string) => {
      const currentSelectedEndTime = watch(finalEndTimeProps.name) as
        BookingScheduleTime | undefined;
      const newSelectedStartTime = (startTimeOptions || []).find(
        (timeObj) => timeObj.time_index === parseInt(newStartTimeIndex),
      );
      const newTimeSelectValue = {
        endTime: currentSelectedEndTime,
        startTime: newSelectedStartTime,
      };

      // clear end time if start time is greater than end time
      const endTimeIndex = currentSelectedEndTime?.time_index || -1;
      if (
        isNumber(newSelectedStartTime?.time_index) &&
        newSelectedStartTime.time_index > endTimeIndex
      ) {
        newTimeSelectValue.endTime = undefined;
      }

      const autoCorrectedValue = getAutoCorrectedValue(newTimeSelectValue);

      // set the start time value
      setValue(finalStartTimeProps.name, newSelectedStartTime);
      onValueChange?.(finalStartTimeProps.name, newSelectedStartTime!);

      // if the end time is auto-corrected, update the end time value
      if (
        autoCorrectedValue?.endTime?.time_index !==
        currentSelectedEndTime?.time_index
      ) {
        setValue(finalEndTimeProps.name, autoCorrectedValue.endTime);
        onValueChange?.(finalEndTimeProps.name, autoCorrectedValue.endTime!);
      }
    };

    // clear end time value if it is not in the end time options
    useEffect(() => {
      if (
        selectedEndTime &&
        !endTimeOptions.some(
          (time) => time.time_index === selectedEndTime.time_index,
        )
      ) {
        const fallbackEndTime = autoCorrectConfig?.minDuration
          ? getFirstAvailableEndTime()
          : null;
        setValue(finalEndTimeProps.name, fallbackEndTime ?? null);
        onValueChange?.(finalEndTimeProps.name, fallbackEndTime ?? null);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [endTimeOptions, selectedEndTime]);

    // autocorrect the initial value when the component is mounted
    // autocorrect the value when the end/start options have changed
    useEffect(() => {
      if (!autoCorrectConfig || !selectedStartTime) {
        return;
      }

      const initialValue = {
        startTime: selectedStartTime,
        endTime: selectedEndTime,
      };

      const autoCorrectedValue = getAutoCorrectedValue(initialValue);
      const resolvedEndTime =
        autoCorrectConfig?.minDuration &&
        autoCorrectedValue.endTime &&
        !endTimeOptions.some(
          (time) => time.time_index === autoCorrectedValue.endTime?.time_index,
        )
          ? getFirstAvailableEndTime()
          : autoCorrectedValue.endTime;
      const resolvedValue = {
        ...autoCorrectedValue,
        endTime: resolvedEndTime,
      };
      if (!isEqual(resolvedValue, initialValue)) {
        setValue(finalEndTimeProps.name, resolvedValue.endTime ?? null);
        onValueChange?.(finalEndTimeProps.name, resolvedValue.endTime ?? null);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [endTimeOptions, selectedEndTime]);

    return (
      <div
        className={cn(
          "tw-flex",
          {
            "tw-w-full": fullWidth,
          },
          className,
        )}
        {...containerProps}
      >
        <SelectField
          fullWidth={true}
          prefix={displayTimeIcon ? <TimeIcon /> : null}
          testId={testId ? `${testId}-start-time` : undefined}
          value={String(selectedStartTime?.time_index ?? "")}
          onValueChange={handleStartTimeChange}
          renderContent={() => (
            <SelectContent className={contentClassName}>
              {(startTimeOptions || []).map((option: BookingScheduleTime) => {
                const { time_index } = option;
                return (
                  <SelectItem
                    ref={(el) => {
                      "number" === typeof time_index &&
                        (selectItemRefs.current[`start-${time_index}`] = el);
                    }}
                    key={option.time_index}
                    value={String(option.time_index)}
                    disabled={
                      !option.available && option.available !== undefined
                    }
                  >
                    {option.time}
                  </SelectItem>
                );
              })}
            </SelectContent>
          )}
          {...finalStartTimeProps}
          className={cn(
            "tw-flex-1",
            {
              "tw-max-w-[50%] tw-pr-8": showEndTime,
            },
            finalStartTimeProps.className,
          )}
          disabled={disabled || finalStartTimeProps.disabled}
        />

        {showEndTime && (
          <SelectField
            fullWidth={true}
            testId={testId ? `${testId}-end-time` : undefined}
            value={String(selectedEndTime?.time_index ?? "")}
            prefix={displayTimeIcon ? <TimeIcon /> : null}
            onValueChange={(timeIndex) => {
              const newSelectedEndTime = (endTimeOptions || []).find(
                (timeObj) => timeObj.time_index === parseInt(timeIndex),
              );

              setValue(finalEndTimeProps.name, newSelectedEndTime);
              onValueChange?.(finalEndTimeProps.name, newSelectedEndTime!);
            }}
            renderContent={() => (
              <SelectContent className={contentClassName}>
                {(endTimeOptions || []).map((option: BookingScheduleTime) => {
                  const { time_index } = option;
                  return (
                    <SelectItem
                      ref={(el) => {
                        "number" === typeof time_index &&
                          (selectItemRefs.current[`end-${time_index}`] = el);
                      }}
                      key={option.time_index}
                      value={String(option.time_index)}
                      disabled={
                        !option.available && option.available !== undefined
                      }
                    >
                      {option.time}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            )}
            {...finalEndTimeProps}
            className={cn(
              "tw-flex-1 tw-max-w-[50%] tw-pl-8",
              finalEndTimeProps.className,
            )}
            disabled={
              disabled ||
              !!autoCorrectConfig?.exactDuration ||
              finalEndTimeProps.disabled ||
              !selectedStartTime ||
              (!selectedStartTime.available &&
                selectedStartTime.available !== undefined)
            }
          />
        )}
      </div>
    );
  },
);

TimeSelectField.displayName = "TimeSelectField";
